/** @jsxImportSource preact */
import type {
  CustomerAuth,
  VerifyEmailAction,
} from "../../protocol/customer-auth";
import { EmailVerificationFlow } from "./email-verification-flow";
import { differenceInMilliseconds, parseISO } from "date-fns";
import { useEffect, useState } from "preact/hooks";

import type { VerificationPrompt } from "../../conversation/verification";
import * as m from "../../paraglide/messages.js";
import { CARD_STYLES } from "../form/card-styles";
import { CardActions } from "../cards/card-actions";
import { ActionCard } from "../cards/action-card";
import { StatusCard } from "../cards/status-card";
import { FORM_STYLES, formVars } from "../form/form-styles";
import { TextField } from "../form/text-field";
import type { WidgetTokens } from "../theme";

/** 商家填的是别人的邮箱:不让浏览器自动填入商家自己的。 */
const NO_AUTOCOMPLETE = "off";

interface CustomerVerificationCardProps {
  prompt: VerificationPrompt;
  tokens: WidgetTokens;
  title?: string;
  description?: string;
  busy?: boolean;
  /** Playground:没有店铺登录,改成填店内顾客的邮箱。 */
  playground?: boolean;
  auth?: CustomerAuth;
  onVerifyEmail?: VerifyEmailAction;
  actions?: ("store_login" | "email_code")[];
  onVerify?: (email?: string) => void;
  onContactStore?: () => void;
  animation?: string;
}

/**
 * 登录提示:店面整页去店铺自己的登录页,顾客资料与验证码都在 Shopify
 * 页面处理;Playground 由商家填店内顾客的邮箱,以该顾客身份继续测试。
 */
export function CustomerVerificationCard({
  prompt,
  tokens,
  busy = false,
  playground: askEmail = false,
  auth,
  onVerifyEmail,
  actions = ["store_login", "email_code"],
  onVerify,
  onContactStore,
  animation,
  title = m.widget_verification_request_title(),
  description = m.widget_verification_request_description(),
}: CustomerVerificationCardProps) {
  const [emailOpen, setEmailOpen] = useState(!!auth?.pending_email_code);
  const [email, setEmail] = useState("");
  const remaining = () =>
    differenceInMilliseconds(parseISO(prompt.expiresAt), new Date());
  const [timedOut, setTimedOut] = useState(() => !(remaining() > 0));
  useEffect(() => {
    const ms = remaining();
    setTimedOut(!(ms > 0));
    if (!(ms > 0)) return;
    const timer = setTimeout(
      () => {
        setTimedOut(true);
      },
      Math.min(ms, 2_147_483_647),
    );
    return () => {
      clearTimeout(timer);
    };
  }, [prompt.expiresAt]);
  const contactAction = onContactStore
    ? {
        label: m.widget_verify_contact_store(),
        onClick: onContactStore,
        disabled: busy,
        variant: "ghost" as const,
      }
    : undefined;
  const expired = timedOut || prompt.lifecycle !== "active";
  if (expired)
    return (
      <StatusCard
        tokens={tokens}
        icon="lock"
        title={title}
        description={m.widget_verification_request_expired()}
        live={!!onVerify}
        actions={contactAction ? [contactAction] : []}
      />
    );
  const action = {
    label: busy
      ? m.widget_verification_request_pending()
      : askEmail
        ? m.widget_playground_customer_action()
        : m.widget_verification_request_action(),
    disabled: busy || !onVerify || (askEmail && email.trim() === ""),
    onClick: () => {
      onVerify?.(askEmail ? email.trim() : undefined);
    },
  };
  const emailAction =
    onVerifyEmail &&
    auth?.email_code !== "unavailable" &&
    actions.includes("email_code")
      ? {
          label: m.widget_verify_email_action(),
          disabled: busy,
          onClick: () => {
            setEmailOpen(true);
          },
        }
      : undefined;
  if ((emailOpen || !!auth?.pending_email_code) && onVerifyEmail)
    return (
      <EmailVerificationFlow
        key={auth?.pending_email_code?.challenge_id ?? "email"}
        tokens={tokens}
        playground={askEmail}
        auth={auth}
        onVerify={onVerifyEmail}
        onContactStore={onContactStore}
      />
    );
  if (!askEmail)
    return (
      <div style={{ animation }}>
        <ActionCard
          tokens={tokens}
          icon="user"
          title={title}
          description={description}
          actions={[
            ...(onVerify && actions.includes("store_login") ? [action] : []),
            ...(emailAction ? [emailAction] : []),
            ...(contactAction ? [contactAction] : []),
          ]}
        />
      </div>
    );
  return (
    <section
      class="gn-card"
      aria-label={title}
      style={{ ...formVars(tokens), animation }}
    >
      <style>{`${FORM_STYLES}${CARD_STYLES}`}</style>
      <h3 class="gn-card-title">{title}</h3>
      <div class="gn-card-body">
        <div class="gn-caption">
          {m.widget_playground_customer_description()}
        </div>
        <TextField
          autoComplete={NO_AUTOCOMPLETE}
          disabled={busy}
          inputMode="email"
          label={m.widget_playground_customer_email_label()}
          onValueChange={setEmail}
          tokens={tokens}
          type="email"
          value={email}
        />
      </div>
      <CardActions
        tokens={tokens}
        actions={[
          ...(onVerify && actions.includes("store_login") ? [action] : []),
          ...(emailAction ? [emailAction] : []),
          ...(contactAction ? [contactAction] : []),
        ]}
      />
    </section>
  );
}
